// src/pages/supplier/InvoiceDiscountingPage.jsx
// Phase 4 — M4: Supplier invoice discounting — early payment on delivered orders

import { useEffect, useState } from 'react'
import { financeApi } from '../../api/services'

const C = { pink: '#FF3D7F', orange: '#FF8500', ink: '#1A1A2E', muted: '#9CA3AF', green: '#10B981', amber: '#F59E0B' }

function StatusPill({ status }) {
  const map = { pending: [C.amber, 'Pending'], approved: ['#3B82F6', 'Approved'], disbursed: [C.green, 'Disbursed'], rejected: ['#EF4444', 'Rejected'], cancelled: [C.muted, 'Cancelled'] }
  const [color, label] = map[status] ?? [C.muted, status]
  return <span style={{ background: `${color}18`, color, padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>{label}</span>
}

export default function InvoiceDiscountingPage() {
  const [data, setData]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [submitting, setSub]    = useState(null)  // order_id
  const [tab, setTab]           = useState('eligible')

  const load = () => {
    setLoading(true)
    financeApi.supplier.overview()
      .then(r => setData(r.data?.data ?? r.data))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  async function submitRequest(orderId) {
    setSub(orderId)
    try {
      await financeApi.supplier.submitDiscountRequest(orderId)
      load()
    } catch (e) {
      alert(e.response?.data?.message ?? 'Failed to submit request.')
    } finally { setSub(null) }
  }

  async function cancelRequest(id) {
    if (!confirm('Cancel this discounting request?')) return
    try {
      await financeApi.supplier.cancelDiscountRequest(id)
      load()
    } catch (e) { alert('Could not cancel.') }
  }

  if (loading) return <div style={{ color: C.muted, textAlign: 'center', padding: 60 }}>Loading invoice discounting…</div>

  const eligible = data?.eligible_orders ?? []
  const requests = data?.requests ?? []

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.8rem', color: C.ink, margin: '0 0 4px' }}>
          Invoice Discounting
        </h1>
        <p style={{ color: C.muted, margin: 0, fontSize: '0.88rem' }}>
          Get paid early on delivered orders — at a small discount. Stop waiting for standard payout cycles.
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Eligible Orders', value: eligible.length, icon: '📦' },
          { label: 'Total Discounted', value: `₹${(data?.total_discounted ?? 0).toLocaleString('en-IN')}`, icon: '💰' },
          { label: 'Fees Paid', value: `₹${(data?.total_fees_paid ?? 0).toLocaleString('en-IN')}`, icon: '📊' },
          { label: 'Active Requests', value: requests.filter(r => ['pending','approved'].includes(r.status)).length, icon: '⏳' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', border: '1.5px solid #F0F0F0' }}>
            <p style={{ fontSize: '1.4rem', margin: '0 0 6px' }}>{s.icon}</p>
            <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.4rem', color: C.ink, margin: 0 }}>{s.value}</p>
            <p style={{ color: C.muted, fontSize: '0.74rem', margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Explainer box */}
      <div style={{ background: 'linear-gradient(135deg, #FFF8F0, #FFF0F5)', border: '1.5px solid #FECBA1', borderRadius: 16, padding: '16px 20px', marginBottom: 24 }}>
        <h3 style={{ fontWeight: 800, color: C.ink, margin: '0 0 8px', fontSize: '0.9rem' }}>💡 How it works</h3>
        <p style={{ color: '#6B7280', fontSize: '0.82rem', lineHeight: 1.7, margin: 0 }}>
          Select any delivered order below and request early payment. Fabrinza pays you immediately at a <b>2% discount fee</b>. Your standard payout cycle is bypassed — no waiting 15–30 days for settlement.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, background: '#fff', borderRadius: 14, padding: 5, border: '1.5px solid #F0F0F0', width: 'fit-content' }}>
        {[['eligible', `📦 Eligible (${eligible.length})`], ['requests', `📋 My Requests (${requests.length})`]].map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 18px', borderRadius: 10, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', border: 'none',
            background: tab === key ? `linear-gradient(135deg, ${C.pink}, ${C.orange})` : 'transparent',
            color: tab === key ? '#fff' : C.muted, transition: 'all 0.2s',
          }}>{lbl}</button>
        ))}
      </div>

      {/* ── ELIGIBLE ORDERS ─────────────────────────────────────────────── */}
      {tab === 'eligible' && (
        <div>
          {eligible.length === 0
            ? <Empty icon="📦" msg="No eligible orders right now. Orders become eligible once marked as delivered and not yet paid out." />
            : (
              <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                  <thead>
                    <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #F0F0F0' }}>
                      {['Order', 'Buyer', 'Invoice', 'Fee (2%)', 'You Receive', ''].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: C.muted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {eligible.map(o => (
                      <tr key={o.order_id} style={{ borderBottom: '1px solid #F9F9F9' }}>
                        <td style={{ padding: '14px 16px', fontWeight: 700, color: C.ink }}>{o.order_number}</td>
                        <td style={{ padding: '14px 16px', color: C.muted }}>{o.buyer_name}</td>
                        <td style={{ padding: '14px 16px', fontWeight: 700 }}>₹{o.supplier_payout?.toLocaleString('en-IN')}</td>
                        <td style={{ padding: '14px 16px', color: '#EF4444' }}>−₹{o.discount_fee?.toLocaleString('en-IN')}</td>
                        <td style={{ padding: '14px 16px', fontWeight: 800, color: C.green }}>₹{o.advance_amount?.toLocaleString('en-IN')}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <button
                            onClick={() => submitRequest(o.order_id)}
                            disabled={submitting === o.order_id}
                            style={{
                              padding: '7px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                              background: submitting === o.order_id ? '#E5E7EB' : `linear-gradient(135deg, ${C.pink}, ${C.orange})`,
                              color: '#fff', fontWeight: 700, fontSize: '0.8rem',
                            }}
                          >{submitting === o.order_id ? 'Submitting…' : 'Request Early Pay'}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      )}

      {/* ── MY REQUESTS ─────────────────────────────────────────────────── */}
      {tab === 'requests' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {requests.length === 0
            ? <Empty icon="📋" msg="No discounting requests yet. Submit one from the Eligible Orders tab." />
            : requests.map(req => (
              <div key={req.id} style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
                  <div>
                    <p style={{ fontWeight: 800, color: C.ink, margin: '0 0 2px', fontSize: '0.9rem' }}>{req.request_number}</p>
                    <p style={{ color: C.muted, fontSize: '0.76rem', margin: 0 }}>Order {req.order?.order_number}</p>
                  </div>
                  <StatusPill status={req.status} />
                </div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: '0.8rem', color: C.muted }}>
                  <span>Invoice: <b style={{ color: C.ink }}>₹{req.invoice_amount?.toLocaleString('en-IN')}</b></span>
                  <span>Fee: <b style={{ color: '#EF4444' }}>₹{req.discount_fee?.toLocaleString('en-IN')}</b></span>
                  <span>Advance: <b style={{ color: C.green }}>₹{req.advance_amount?.toLocaleString('en-IN')}</b></span>
                  {req.disbursement_reference && <span>UTR: <b style={{ color: C.ink }}>{req.disbursement_reference}</b></span>}
                </div>
                {req.status === 'pending' && (
                  <button onClick={() => cancelRequest(req.id)} style={{ marginTop: 12, padding: '5px 14px', border: '1.5px solid #E5E7EB', borderRadius: 8, background: '#fff', color: C.muted, fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>
                    Cancel Request
                  </button>
                )}
                {req.rejection_reason && <p style={{ marginTop: 8, color: '#EF4444', fontSize: '0.78rem' }}>Reason: {req.rejection_reason}</p>}
              </div>
            ))
          }
        </div>
      )}
    </div>
  )
}

function Empty({ icon, msg }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0' }}>
      <p style={{ fontSize: '2.5rem', marginBottom: 10 }}>{icon}</p>
      <p style={{ color: C.muted, fontSize: '0.85rem', maxWidth: 360, margin: '0 auto' }}>{msg}</p>
    </div>
  )
}
